import React, { useState, useCallback } from "react";
import { ethers } from "ethers";
import { VaultCard } from "./components/VaultCard";
import { useVaults } from "./hooks/useVaults";
import { BASE_CHAIN_ID, COLORS } from "./constants";

declare global {
  interface Window {
    ethereum?: ethers.Eip1193Provider & {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
    };
  }
}

export default function App() {
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner]     = useState<ethers.Signer | null>(null);
  const [address, setAddress]   = useState<string | null>(null);
  const [connErr, setConnErr]   = useState<string | null>(null);

  const { vaults, loading, error, refresh } = useVaults(address, provider);

  const connect = useCallback(async () => {
    setConnErr(null);
    if (!window.ethereum) {
      setConnErr("No wallet detected. Install MetaMask or Rabby.");
      return;
    }
    try {
      const prov    = new ethers.BrowserProvider(window.ethereum);
      const network = await prov.getNetwork();

      if (Number(network.chainId) !== BASE_CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${BASE_CHAIN_ID.toString(16)}` }],
          });
        } catch {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId:           `0x${BASE_CHAIN_ID.toString(16)}`,
              chainName:         "Base",
              nativeCurrency:    { name: "Ether", symbol: "ETH", decimals: 18 },
              rpcUrls:           ["https://mainnet.base.org"],
              blockExplorerUrls: ["https://basescan.org"],
            }],
          });
        }
      }

      const sign = await prov.getSigner();
      const addr = await sign.getAddress();
      setProvider(prov);
      setSigner(sign);
      setAddress(addr);
    } catch (e: unknown) {
      setConnErr(e instanceof Error ? e.message : "Connection failed");
    }
  }, []);

  const disconnect = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
  }, []);

  // Aggregated protocol stats
  const totalTvlLp = vaults.reduce((sum, v) => sum + parseFloat(v.totalLpManaged || "0"), 0);
  const totalPending = vaults.reduce((sum, v) => sum + parseFloat(v.pendingRewards || "0"), 0);
  const totalCompounded = vaults.reduce((sum, v) => sum + parseFloat(v.totalRewardsCompounded || "0"), 0);
  const totalFees = vaults.reduce((sum, v) => sum + parseFloat(v.totalFeesCollected || "0"), 0);
  const userTotalLp = vaults.reduce((sum, v) => sum + parseFloat(v.userLpValue || "0"), 0);
  const hasAnyPosition = vaults.some(v => parseFloat(v.userShares) > 0);

  return (
    <div style={s.root}>
      <div style={s.container}>

        {/* ── Header ────────────────────────────────────────── */}
        <header style={s.header}>
          <div>
            <div style={s.logoRow}>
              <span style={s.logoIcon}>⚡</span>
              <span style={s.logoText}>AeroCompounder</span>
              <span style={s.networkBadge}>Base</span>
            </div>
            <p style={s.tagline}>Auto-compound your Aerodrome LP rewards</p>
          </div>

          {address ? (
            <div style={s.walletBox}>
              <div style={s.walletAddr}>
                {address.slice(0, 6)}…{address.slice(-4)}
              </div>
              <button style={s.disconnectBtn} onClick={disconnect}>✕</button>
            </div>
          ) : (
            <button style={s.connectBtn} onClick={connect}>
              Connect Wallet
            </button>
          )}
        </header>

        {/* ── Error ─────────────────────────────────────────── */}
        {(connErr || error) && (
          <div style={s.errBox}>{connErr || error}</div>
        )}

        {/* ── Protocol Stats ────────────────────────────────── */}
        {provider && vaults.length > 0 && (
          <div style={s.protocolStats}>
            <ProtocolStat label="Vaults"            value={`${vaults.length}`} />
            <ProtocolStat label="Total LP Managed"  value={fmtNum(totalTvlLp, 6) + " LP"} />
            <ProtocolStat label="Pending Rewards"   value={fmtNum(totalPending, 4) + " AERO"} accent />
            <ProtocolStat label="Total Compounded"  value={fmtNum(totalCompounded, 4) + " AERO"} />
            <ProtocolStat label="Fees Collected"    value={fmtNum(totalFees, 4) + " AERO"} />
            {hasAnyPosition && (
              <ProtocolStat label="Your Total LP"   value={fmtNum(userTotalLp, 6) + " LP"} accent />
            )}
          </div>
        )}

        {/* ── No wallet ─────────────────────────────────────── */}
        {!address && (
          <div style={s.connectPrompt}>
            <div style={s.promptIcon}>🌊</div>
            <h2 style={s.promptTitle}>Auto-compound your Aerodrome yields</h2>
            <p style={s.promptText}>
              Deposit your Aerodrome LP tokens and let AeroCompounder harvest,
              swap, and reinvest your AERO rewards automatically.
            </p>
            <ul style={s.promptList}>
              <li>2% performance fee on harvested rewards</li>
              <li>0.1% withdrawal fee</li>
              <li>Non-custodial — withdraw anytime</li>
              <li>Multiple pools supported</li>
            </ul>
            <button style={s.connectBtnLarge} onClick={connect}>
              Connect Wallet to Start
            </button>
          </div>
        )}

        {/* ── Vaults ────────────────────────────────────────── */}
        {provider && (
          <>
            {loading && vaults.length === 0 ? (
              <div style={s.loading}>Loading vaults...</div>
            ) : vaults.length === 0 ? (
              <div style={s.empty}>No vaults deployed yet.</div>
            ) : (
              <>
                <div style={s.sectionHeader}>
                  <h2 style={s.sectionTitle}>Vaults</h2>
                  <button style={s.refreshBtn} onClick={refresh}>↻ Refresh</button>
                </div>
                {vaults.map((vault) => (
                  <VaultCard
                    key={vault.address}
                    vault={vault}
                    signer={signer}
                    onRefresh={refresh}
                  />
                ))}
              </>
            )}
          </>
        )}

        {/* ── Footer ────────────────────────────────────────── */}
        <footer style={s.footer}>
          <div style={s.footerLinks}>
            <a href="https://x.com/aerocompounder" style={s.footerXLink} target="_blank" rel="noreferrer">
              <svg style={{ display: "block" }} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.631L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
              </svg>
              @aerocompounder
            </a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://aerocompounder.com" style={s.footerLink}>aerocompounder.com</a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://aerodrome.finance" style={s.footerLink} target="_blank" rel="noreferrer">Aerodrome</a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://basescan.org" style={s.footerLink} target="_blank" rel="noreferrer">BaseScan</a>
          </div>
          <p style={s.footerText}>
            Non-custodial. Audits pending. Use at your own risk.
          </p>
        </footer>

      </div>
    </div>
  );
}

function fmtNum(n: number, decimals = 4): string {
  if (n === 0) return "0";
  if (n > 0 && n < Math.pow(10, -decimals)) {
    const magnitude = Math.floor(Math.log10(n));
    const sigFigDecimals = Math.abs(magnitude) + 2;
    return n.toFixed(Math.min(sigFigDecimals, 12));
  }
  return n.toFixed(decimals);
}

const ProtocolStat: React.FC<{ label: string; value: string; accent?: boolean }> = ({
  label, value, accent,
}) => (
  <div style={{ textAlign: "center" as const }}>
    <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 20, fontWeight: 800, color: accent ? COLORS.green : COLORS.textPrimary }}>
      {value}
    </div>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight: "100vh",
    background: COLORS.bg,
    color: COLORS.textPrimary,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: "24px 16px 48px",
  },
  container: { maxWidth: 520, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28,
  },
  logoRow:  { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  logoIcon: { fontSize: 22 },
  logoText: { fontSize: 22, fontWeight: 900, color: COLORS.textPrimary, letterSpacing: "-0.5px" },
  networkBadge: {
    fontSize: 11,
    background: "#1e2d5a",
    color: "#60a5fa",
    border: "1px solid #1e40af44",
    borderRadius: 4,
    padding: "2px 8px",
    fontWeight: 700,
  },
  tagline: { margin: 0, fontSize: 13, color: COLORS.textMuted },
  walletBox: { display: "flex", alignItems: "center", gap: 6 },
  walletAddr: {
    fontSize: 13,
    color: COLORS.textSub,
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    padding: "6px 12px",
    borderRadius: 8,
  },
  disconnectBtn: {
    padding: "6px 8px",
    borderRadius: 6,
    border: "none",
    background: COLORS.bgCard,
    color: COLORS.textMuted,
    cursor: "pointer",
    fontSize: 12,
  },
  connectBtn: {
    padding: "10px 18px",
    borderRadius: 8,
    border: "none",
    background: COLORS.primary,
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  errBox: {
    background: "#1f1015",
    border: "1px solid #7f1d1d",
    color: COLORS.red,
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
  },
  protocolStats: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 16,
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  connectPrompt: {
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: 32,
    textAlign: "center" as const,
    marginBottom: 24,
  },
  promptIcon:  { fontSize: 40, marginBottom: 12 },
  promptTitle: { margin: "0 0 12px", fontSize: 20, fontWeight: 800, color: COLORS.textPrimary },
  promptText:  { color: COLORS.textSub, fontSize: 14, lineHeight: 1.6, margin: "0 0 16px" },
  promptList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 24px",
    color: COLORS.textMuted,
    fontSize: 13,
    lineHeight: 2,
  },
  connectBtnLarge: {
    padding: "14px 32px",
    borderRadius: 10,
    border: "none",
    background: COLORS.primary,
    color: "#fff",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.textSub },
  refreshBtn: {
    padding: "4px 10px",
    borderRadius: 6,
    border: `1px solid ${COLORS.border}`,
    background: "transparent",
    color: COLORS.textMuted,
    cursor: "pointer",
    fontSize: 12,
  },
  loading: { color: COLORS.textMuted, textAlign: "center" as const, padding: 48 },
  empty:   { color: COLORS.textMuted, textAlign: "center" as const, padding: 48 },
  footer:  { marginTop: 32, textAlign: "center" as const },
  footerLinks: { display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" as const },
  footerLink:  { color: COLORS.textMuted, fontSize: 12, textDecoration: "none" },
  footerXLink: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    color: COLORS.textSub,
    fontSize: 12,
    textDecoration: "none",
    fontWeight: 600,
  },
  footerText: { color: "#374151", fontSize: 11, margin: 0 },
};
